// Explanatory copy for each tool's "How & Why" page. Lighter than the FOCUS·ARMOR deep-dive.
// focus-armor has its own dedicated detailed page (/guide/focus-armor).
export const TOOL_GUIDES = {
  eisenhower: {
    what: "A four-quadrant grid that sorts every commitment by two questions only: is it urgent, and is it important? The combination tells you exactly what to do next with each item.",
    when: [
      "Your task list is long, conflicting, and everything feels equally pressing.",
      "You are reacting to other people's urgency instead of your own priorities.",
      "You need to justify — to yourself or a team — what gets dropped or delegated.",
    ],
    how: [
      "List every open commitment without filtering.",
      "Tag each as Urgent / Not Urgent and Important / Not Important.",
      "Place it in one of the four quadrants: Do (urgent + important), Schedule (important, not urgent), Delegate (urgent, not important), Delete (neither).",
      "Act on the quadrant, not the feeling: protect 'Schedule', delegate ruthlessly, and delete without guilt.",
    ],
    example: "A plant manager facing 20 'fires' finds only 4 are genuinely important-and-urgent; eight are delegated to shift leads and five are deleted outright — freeing a full day for the strategic 'Schedule' work that never got done.",
  },
  playing_to_win: {
    what: "A strategy cascade of five linked choices that turns vague ambition into a concrete, testable strategy: a winning aspiration, where to play, how to win, the capabilities required, and the management systems to sustain it.",
    when: [
      "Setting or resetting direction for a business, function or product line.",
      "Your 'strategy' is really a list of goals or initiatives, not a set of choices.",
      "Teams are busy but pulling in different directions.",
    ],
    how: [
      "Define the winning aspiration — what does winning actually look like, and for whom?",
      "Choose where to play — the specific markets, segments, channels and geographies.",
      "Decide how to win — the distinctive way you will create value where you play.",
      "Name the capabilities you must have to win there.",
      "Specify the management systems that build and sustain those capabilities.",
    ],
    example: "A regional distributor stops trying to serve everyone, chooses to win on same-day reliability for industrial buyers, and reorganises its fleet and IT systems around that single promise.",
  },
  five_forces: {
    what: "A structured scan of the five competitive pressures that determine how attractive — and how profitable — an industry really is: new entrants, supplier power, buyer power, substitutes, and rivalry.",
    when: [
      "Entering a new market or evaluating an existing one's long-term profitability.",
      "Profits are eroding and you need to know which pressure is squeezing you.",
      "Pressure-testing an investment, acquisition or expansion thesis.",
    ],
    how: [
      "For each of the five forces, rate the pressure as Low / Medium / High.",
      "Capture the evidence and drivers behind each rating in the notes.",
      "Identify which forces dominate the economics of your industry.",
      "Translate the diagnosis into where you defend, where you build power, and where you avoid.",
    ],
    example: "A SaaS founder discovers buyer power and rivalry are both High while switching costs are Low — pointing the strategy squarely at deepening product lock-in and differentiation.",
  },
  vrio: {
    what: "A four-gate test that judges whether a specific capability or resource is a genuine, durable competitive advantage: is it Valuable, Rare, hard to Imitate, and is the Organisation set up to exploit it?",
    when: [
      "Deciding where to invest to build lasting advantage.",
      "Separating real moats from things that merely feel like strengths.",
      "Justifying why a capability deserves protection or further funding.",
    ],
    how: [
      "Name the capability or resource precisely.",
      "Answer the four gates in order — Valuable? Rare? Costly to imitate? Organised to capture value?",
      "Read the verdict: a No at any gate caps the advantage (parity, temporary, or unused potential).",
      "Direct investment toward capabilities that clear all four gates.",
    ],
    example: "A consultancy's proprietary methodology is valuable and rare but easily copied — VRIO reveals it as a temporary edge, prompting a push to embed it in tooling that is far harder to imitate.",
  },
  swot: {
    what: "The classic 2x2 baseline: internal Strengths and Weaknesses set against external Opportunities and Threats — a fast, shared snapshot to anchor a strategy conversation.",
    when: [
      "Kicking off planning and you need a common starting picture quickly.",
      "Onboarding a team or stakeholder to the current situation.",
      "Pairing internal reality with the external environment before deciding.",
    ],
    how: [
      "List internal Strengths — what you do demonstrably well.",
      "List internal Weaknesses — honest gaps and constraints.",
      "List external Opportunities — favourable shifts you could exploit.",
      "List external Threats — risks and headwinds outside your control.",
      "Cross them: use strengths on opportunities, shore up weaknesses against threats.",
    ],
    example: "A clinic maps a strong reputation (S) against a new competitor opening nearby (T) and decides to convert loyal patients into a referral programme before the competitor lands.",
  },
  long_pestel: {
    what: "A macro-environment scan across six external vectors — Political, Economic, Social, Technological, Environmental and Legal — viewed at three horizons: Local, National and Global (LoNG).",
    when: [
      "Assessing forces outside your control before a major commitment.",
      "Expanding into a new geography or regulatory context.",
      "Building scenarios and stress-testing long-range plans.",
    ],
    how: [
      "For each vector, capture the relevant factors at Local, National and Global scope.",
      "Flag which factors are accelerating, stable, or declining.",
      "Identify the two or three factors most likely to reshape your operating context.",
      "Feed those into strategy, risk registers and scenario planning.",
    ],
    example: "A manufacturer scanning Environmental + Legal at the national horizon spots incoming packaging regulation 18 months out, and starts a redesign before it becomes a compliance scramble.",
  },
  sipoc: {
    what: "A one-page map of a process at the highest useful level: Suppliers, Inputs, Process, Outputs and Customers — the shared definition of scope before any improvement work begins.",
    when: [
      "Starting a process-improvement effort and the boundaries are unclear.",
      "Different stakeholders describe the same process differently.",
      "Onboarding or handing over ownership of a workflow.",
    ],
    how: [
      "Name the process and its 5–7 high-level steps.",
      "List the Outputs and the Customers who receive them.",
      "List the Inputs required and the Suppliers who provide them.",
      "Confirm the start and end points so scope is unambiguous.",
    ],
    example: "Before fixing 'slow invoicing', a finance team draws the SIPOC and discovers the real delay sits with a supplier input upstream — not in their own process at all.",
  },
  dmaic: {
    what: "A disciplined, data-driven improvement cycle — Define, Measure, Analyse, Improve, Control — for solving a problem at its root and making the fix stick.",
    when: [
      "A recurring problem has resisted quick fixes.",
      "You need evidence, not opinion, to find and prove the cause.",
      "An improvement must be sustained, not just achieved once.",
    ],
    how: [
      "Define the problem, goal and scope.",
      "Measure the current performance with real data.",
      "Analyse to find the true root cause(s).",
      "Improve by designing, testing and implementing the fix as trackable tasks.",
      "Control with monitoring and standards so the gain holds.",
    ],
    example: "A support team cuts average resolution time by a third — but only the Control phase (a weekly SLA dashboard) stops it from drifting back within a quarter.",
  },
  toc: {
    what: "Theory of Constraints: a system is limited by exactly one bottleneck at a time. Find it, get the most from it, align everything else to it, then lift it — and repeat.",
    when: [
      "Throughput is capped and adding effort everywhere isn't helping.",
      "You suspect local 'efficiencies' are not improving the whole.",
      "You need the single highest-leverage place to act.",
    ],
    how: [
      "Identify the constraint limiting the whole system.",
      "Exploit it — squeeze maximum output from the bottleneck as-is.",
      "Subordinate everything else to the pace of the constraint.",
      "Elevate the constraint — invest to expand its capacity.",
      "Repeat: once broken, find the next constraint.",
    ],
    example: "A workshop stops optimising every station and instead keeps one CNC machine running through lunch breaks — lifting total output far more than the previous scattered improvements ever did.",
  },
  ccc: {
    what: "A fast, structured incident-solver: state the Concern precisely, drive to root Cause with 5-Whys, then define and track Countermeasures to closure.",
    when: [
      "A specific defect, failure or incident needs a quick, rigorous fix.",
      "You want to stop firefighting symptoms and address the real cause.",
      "Countermeasures need owners and follow-through, not just good intentions.",
    ],
    how: [
      "Write the Concern as a sharp, factual problem statement.",
      "Run 5-Whys to move from symptom to root cause.",
      "Define concrete Countermeasures that attack the cause.",
      "Track each countermeasure to completion on the live checklist.",
    ],
    example: "A line stoppage traced through 5-Whys lands not on the failed sensor but on a missing PM schedule — the countermeasure fixes the schedule, not just the sensor.",
  },
  fmea: {
    what: "Failure Mode & Effects Analysis: a proactive grid that scores each potential failure on Severity, Occurrence and Detection, multiplying them into a Risk Priority Number (RPN) to rank what to fix first.",
    when: [
      "Designing or reviewing a process, product or critical asset.",
      "You need to prioritise prevention before failures happen.",
      "Risk discussions are anecdotal and need a defensible ranking.",
    ],
    how: [
      "List the failure modes for each step or component.",
      "Score Severity, Occurrence and Detection (1–10) for each.",
      "Compute RPN = S × O × D to rank the risks.",
      "Attack the highest RPNs first with mitigation actions.",
    ],
    example: "A maintenance team finds a low-severity but frequent, hard-to-detect failure scores a higher RPN than a dramatic-but-rare one — redirecting their next inspection cycle.",
  },
  asset_lifecycle: {
    what: "A portfolio matrix that plots assets or products by market growth and relative share to classify each as a Star, Question Mark, Cash Cow or Dog — and guide where capital should flow.",
    when: [
      "Allocating limited capital across a portfolio of products or assets.",
      "Deciding what to invest in, harvest, or retire.",
      "Balancing today's cash generators against tomorrow's growth.",
    ],
    how: [
      "List each asset/product with its growth, share, margin and consumption.",
      "Plot growth (high/low) against share (high/low).",
      "Read the quadrant: Star (invest), Cash Cow (harvest), Question Mark (decide), Dog (divest).",
      "Reallocate capital from cash cows toward selected stars and question marks.",
    ],
    example: "A product line revealed as a Cash Cow funds two Question Marks; a long-loved but low-growth, low-share Dog is finally retired.",
  },
  calibration: {
    what: "A talent grid that places each team member on two axes — performance and alignment to values — to drive fair, consistent decisions about reward, development and consequence.",
    when: [
      "Running performance or succession reviews across a team.",
      "Reward and development decisions feel inconsistent or biased.",
      "You need a shared, defensible language for talent conversations.",
    ],
    how: [
      "Place each person on the performance vs values grid.",
      "Name the disposition for each cell (e.g., develop, stretch, address, exit).",
      "Calibrate across raters to remove individual bias.",
      "Convert placements into concrete development or action plans.",
    ],
    example: "A high performer who consistently violates values is moved from 'untouchable' to a clear, time-boxed expectations conversation — protecting the culture other strong performers rely on.",
  },
  coaching: {
    what: "A four-step developmental feedback conversation that puts the recipient's own self-reflection first, then layers in the coach's view — deliberately closing on strengths and an agreed action plan. The sequence builds ownership instead of defensiveness.",
    when: [
      "Preparing for a feedback or coaching conversation that matters.",
      "Past feedback was one-directional, defensive, or didn't change anything.",
      "You want the other person to own the insight, not just receive a verdict.",
    ],
    how: [
      "Ask first: 'What do you think is working well?' — let them self-assess strengths.",
      "Ask: 'What do you think you could do differently?' — their own improvement view.",
      "Share: 'What I think you could do differently' — your developmental input, specific and behavioural.",
      "Share: 'What I think you are doing well' — reinforce genuine strengths, sincerely, to close on a high.",
      "Agree an action plan together — concrete, owned and dated.",
    ],
    example: "Instead of opening with criticism, a manager asks the team lead what's working and what they'd change first; the lead names the very issue the manager was worried about — so the coaching input lands as agreement, not attack, and the action plan is owned willingly.",
  },
  communication_strategy: {
    what: "A plan for stakeholder messaging and engagement around a change or initiative — aligning business goals, audiences, key messages and channels so the right people hear the right thing at the right time.",
    when: [
      "Launching a change, initiative or announcement with multiple audiences.",
      "Stakeholders are anxious, misinformed, or hearing mixed messages.",
      "You need engagement and buy-in, not just broadcast.",
    ],
    how: [
      "Clarify the business goals the communication must serve.",
      "Map stakeholders/audiences and what each one needs to know and feel.",
      "Craft tailored key messages per audience.",
      "Choose channels, sequence and cadence — and define how you'll listen back.",
    ],
    example: "Ahead of a reorganisation, leaders brief managers first with a Q&A pack so front-line teams hear the news from their own manager — not the rumour mill.",
  },
  congruence: {
    what: "The Nadler-Tushman Congruence Model diagnoses organisational performance by testing the fit between four components: the Work, the People, the Formal structure, and the Informal culture.",
    when: [
      "Performance is off and the cause isn't obvious.",
      "A strategy is sound on paper but failing in execution.",
      "Post-merger or post-reorg, things just don't 'fit' together.",
    ],
    how: [
      "Describe the critical Work the organisation must do.",
      "Assess the People — skills, motivation and fit to the work.",
      "Examine the Formal structure — roles, processes, metrics, rewards.",
      "Surface the Informal organisation — culture, norms, real power.",
      "Find the misalignments between these four and target the biggest gap.",
    ],
    example: "A company's new collaborative strategy (Work) fails because rewards (Formal) still pay people for individual output — the congruence gap, not effort, is the problem.",
  },
  ansoff: {
    what: "A 2x2 growth matrix that maps four ways to grow — by market (existing or new) against product (existing or new) — and orders them by escalating risk: Market Penetration, Product Development, Market Development and Diversification.",
    when: [
      "Setting a growth agenda and weighing several options at once.",
      "Growth talk is a wish-list with no sense of the relative risk of each path.",
      "Deciding how much of the plan should be safe versus speculative.",
    ],
    how: [
      "List each growth initiative you are considering.",
      "Tag every initiative by market (existing/new) and product (existing/new).",
      "Read the quadrant: Market Penetration (lowest risk), Product Development and Market Development (moderate), Diversification (highest).",
      "Balance the portfolio — fund penetration to pay for a controlled number of higher-risk bets, and pilot diversification before scaling.",
    ],
    example: "A components maker keeps most of its plan in Market Penetration with existing aerospace customers, funds one new medical-housings line (Product Development), and runs an additive-manufacturing diversification only as a small pilot.",
  },
  "nine-box-matrix": {
    what: "A 3x3 talent grid that plots each person on performance against potential, turning a subjective talent debate into a shared map with a clear development or succession path for every cell.",
    when: [
      "Running succession planning or a talent review across a team.",
      "Promotion and development bets are made on gut feel, not a shared view.",
      "You need to spot flight-risk stars and under-developed potential early.",
    ],
    how: [
      "Rate each person Low / Medium / High on current performance and on future potential.",
      "Plot them on the 3x3 grid — the cell defines the talent story.",
      "Assign a disposition path to each: fast-track, stretch, retain, develop, or address.",
      "Convert the grid into concrete succession moves and development actions.",
    ],
    example: "A high-potential graduate sitting in the medium-performance cell is given a stretch assignment and a mentor, while a high-performing specialist with low potential is recognised and retained as a trusted professional rather than pushed into management.",
  },
  "key-person-risk": {
    what: "A dependency register that ranks critical roles by the operational impact of losing the holder and the probability they leave, producing a Vulnerability Rank (Impact × Flight Risk) and flagging the exposures that need a succession plan now.",
    when: [
      "Single individuals hold knowledge or relationships the operation cannot run without.",
      "You need to justify where to invest in cross-training and documentation first.",
      "A valued specialist is a known flight risk and there is no backup.",
    ],
    how: [
      "List each critical position and its current incumbent.",
      "Score Operational Impact of departure (1–10) and Flight Risk probability (1–10).",
      "Read the Vulnerability Rank (Impact × Risk); any role scoring high — or with Impact alone at 9–10 — is a Critical Exposure.",
      "Attack the critical exposures with documented procedures, cross-training and retention or succession plans.",
    ],
    example: "A sole heat-treat setup specialist scores a high impact even at moderate flight risk; flagged as a Critical Exposure, the team captures his setups as written procedures and begins cross-training before he is ever at risk of leaving.",
  },
  "raci-e-grid": {
    what: "An accountability matrix that maps every step of a project or process against the roles involved, marking who is Responsible, Accountable, Consulted, Informed — plus an explicit Enabler/Executor for the specialists who clear blockages. It enforces exactly one accountable owner per step.",
    when: [
      "Work stalls because nobody is clearly accountable — or several people think they are.",
      "Hand-offs are messy and specialists are pulled in too late.",
      "Launching a cross-functional project that needs unambiguous ownership.",
    ],
    how: [
      "List the project steps down the side and the roles across the top.",
      "Mark each cell: R (does the work), A (single owner answerable for the outcome), C (consulted), I (informed), E (enables/executes the technical fix).",
      "Enforce one — and only one — A per step; flag any step with zero or multiple A's.",
      "Resolve the gaps and overlaps before work starts, not after it breaks.",
    ],
    example: "A retrofit project shows two managers both marked Accountable for safety sign-off; the grid surfaces the conflict immediately, the team assigns a single owner, and the specialist contractor is correctly tagged as the Enabler.",
  },
};
